import { useCallback } from 'react';
import { toast } from '@/hooks/use-toast';

export interface UseClearHandlerDeps {
  blockCount: number;
  connectorCount: number;
  clearCanvas: () => void;
  setConnectMode: (v: boolean) => void;
  setGroupMode: (v: boolean) => void;
  setGroupSelection: (ids: string[]) => void;
  setCurrentDocumentId: (id: string | null) => void;
  resetLayerPath: () => void;
  setConfirmClearOpen: (v: boolean) => void;
  logAction: (action: string, detail?: string) => void;
  announce: (msg: string) => void;
  t: (key: string) => string;
}

export function useClearHandler(deps: UseClearHandlersDeps) {
  const {
    blockCount, connectorCount, clearCanvas,
    setConnectMode, setGroupMode, setGroupSelection,
    setCurrentDocumentId, resetLayerPath, setConfirmClearOpen,
    logAction, announce, t,
  } = deps;

  const handleClearRequest = useCallback(() => {
    if (blockCount === 0 && connectorCount === 0) return;
    setConfirmClearOpen(true);
  }, [blockCount, connectorCount, setConfirmClearOpen]);

  const handleClearConfirmed = useCallback(() => {
    const count = blockCount;
    clearCanvas();
    setConnectMode(false);
    setGroupMode(false);
    setGroupSelection([]);
    setCurrentDocumentId(null);
    resetLayerPath();
    logAction('Canvas cleared', `${count} blocks removed`);
    announce(t('canvasCleared'));
    toast({ title: t('cleared'), description: t('canvasCleared') });
  }, [
    blockCount, clearCanvas, setConnectMode, setGroupMode, setGroupSelection,
    setCurrentDocumentId, resetLayerPath, logAction, announce, t,
  ]);

  return { handleClearRequest, handleClearConfirmed };
}

// Type alias for convenience in consumers that want the deps interface.
export type UseClearHandlersDeps = UseClearHandlerDeps;
